/**
 * @module engine/export/exportPng
 * @summary Render the WHOLE diagram to a 2× PNG and download it.
 * @description Renders the full diagram bounding box (not just the current
 *   viewport) to an off-screen canvas at a fixed 2× scale — matching the PDF
 *   exporter's coverage — and stamps a company footer.
 * @ported-from JCC PFD Engineering Tool v1.1.html lines 3763-3778.
 */
import { S } from '../state';
import { setCtx, drawFn, drawLabel } from '../shapes/drawFns';
import { drawConn } from '../routing';
import { computeBalance, streamMaterial, streamRateText } from '../balance';
import { dlBlob } from '../../lib/download';

export function exportPng(): void {
  const n = S.docMeta.docName || 'PFD';
  if (!S.shapes.length) {
    alert('No shapes on canvas to export.');
    return;
  }

  // Tight bounding box over ALL shapes (same approach as the PDF exporter).
  const PAD = 50;
  const xs = S.shapes.map((s) => s.x);
  const ys = S.shapes.map((s) => s.y);
  const xe = S.shapes.map((s) => s.x + s.w);
  const ye = S.shapes.map((s) => s.y + s.h);
  const bx = Math.min(...xs) - PAD;
  const by = Math.min(...ys) - PAD;
  const bw = Math.max(...xe) - Math.min(...xs) + PAD * 2;
  const bh = Math.max(...ye) - Math.min(...ys) + PAD * 2;

  // 2× for crispness, clamped so huge diagrams stay inside canvas size limits.
  const SC = Math.min(2, 16000 / Math.max(bw, bh));
  const FOOTER = 26 * SC;
  const off = document.createElement('canvas');
  off.width = Math.round(bw * SC);
  off.height = Math.round(bh * SC + FOOTER);
  const oc = off.getContext('2d');
  if (!oc) return;

  oc.fillStyle = '#ffffff';
  oc.fillRect(0, 0, off.width, off.height);
  oc.setTransform(SC, 0, 0, SC, -bx * SC, -by * SC);
  S.shapes
    .filter((s) => s.type === 'zonebox')
    .forEach((s) => {
      oc.save();
      drawFn(oc, s);
      oc.restore();
    });
  // The exported picture says what the drawing on screen says. A drawing with
  // no rates typed on it balances to nothing and exports exactly as it always
  // did, so this cannot change an existing sheet.
  const bal = computeBalance();
  S.connections.forEach((cn) =>
    drawConn(oc, cn, false, streamRateText(bal, cn.id), streamMaterial(bal, cn.id)),
  );
  S.shapes
    .filter((s) => s.type !== 'zonebox')
    .sort((a, b) => (a.zIndex || 0) - (b.zIndex || 0))
    .forEach((s) => {
      oc.save();
      setCtx(oc, s);
      if (s.rotation) {
        const cx = s.x + s.w / 2;
        const cy = s.y + s.h / 2;
        oc.translate(cx, cy);
        oc.rotate((s.rotation * Math.PI) / 180);
        oc.translate(-cx, -cy);
      }
      try {
        drawFn(oc, s);
      } catch {
        /* skip a broken symbol */
      }
      oc.restore();
      // Labels outside the rotation transform — see render.ts.
      oc.save();
      setCtx(oc, s);
      drawLabel(oc, s);
      oc.restore();
    });

  // Footer band.
  oc.setTransform(1, 0, 0, 1, 0, 0);
  oc.fillStyle = 'rgba(30,30,30,0.9)';
  oc.fillRect(0, off.height - FOOTER, off.width, FOOTER);
  oc.fillStyle = '#fff';
  oc.font = 'bold ' + 8 * SC + 'px Segoe UI';
  oc.textAlign = 'left';
  oc.fillText(
    'JEDDAH CABLES COMPANY   |   ' + n + '   |   Rev ' + S.docMeta.rev + '   |   ' + S.docMeta.date,
    8 * SC,
    off.height - 10 * SC,
  );
  const safe = n.replace(/[^a-zA-Z0-9_\- ]/g, '');
  off.toBlob((blob) => {
    if (blob) dlBlob(blob, `JCC_PFD_${safe}.png`);
  }, 'image/png');
}
